import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Building2, Calendar, Euro, TrendingUp, Menu, X } from "lucide-react";

// ============ DESIGN SYSTEM ============
const GRID = 8;
const VISUAL_GRID = 40;
const sp = (n) => `${n * GRID}px`;

// ============ DUMMY PROJECTS DATA ============
// Erweitert mit Status, Highlights und mehr visuellen Daten
export const PROJECTS_DATA = [
  {
    id: "ms0273-goldbeck",
    slug: "ms0273-goldbeck",
    title: "Goldbeck Bausch+Ströbel",
    subtitle: "Pharmazeutische Produktion",
    shortDescription: "Komplette Beleuchtungsinstallation und DALI-Steuerung für den Neubau eines Produktionsgebäudes der pharmazeutischen Industrie.",
    description: "Umfassende Planung und Ausführung der gesamten Allgemein- und Sicherheitsbeleuchtung für ein hochmodernes Produktionsgebäude. Das Projekt umfasste die Integration einer komplexen DALI-Steuerung für tageslichtabhängige Regelung und Präsenzerfassung in allen Bereichen.",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=900&fit=crop&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&h=900&fit=crop&q=80",
      "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1600&h=900&fit=crop&q=80",
      "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?w=1600&h=900&fit=crop&q=80"
    ],
    tags: ["Neubau", "Industrie", "DALI-Steuerung"],
    status: "Abgeschlossen",
    highlight: "8.500 Lichtpunkte",
    leistungen: [
      "Planung der Allgemeinbeleuchtung",
      "Sicherheitsbeleuchtung nach DIN EN 1838",
      "DALI-Steuerungstechnik",
      "Montage und Inbetriebnahme",
      "Dokumentation und Einweisung"
    ],
    technischeDaten: {
      auftraggeber: "Goldbeck GmbH",
      projektvolumen: "1.2 Mio. €",
      zeitraum: "März 2024 – November 2024",
      leistungsphase: "LP 5-8",
      flaeche: "12.000 m²"
    }
  },
  {
    id: "amazon-dortmund",
    slug: "amazon-dortmund",
    title: "Amazon Logistikzentrum",
    subtitle: "Dortmund",
    shortDescription: "Nachrüstung und Erweiterung der Beleuchtungsanlage in einem bestehenden Logistikzentrum mit über 50.000 m² Hallenfläche.",
    description: "Technisch anspruchsvolle Sanierung und Erweiterung der Beleuchtung im laufenden Betrieb. Die Herausforderung bestand in der Koordination der Arbeiten während des 24/7-Betriebs ohne Beeinträchtigung der Logistikprozesse.",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1600&h=900&fit=crop&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1600&h=900&fit=crop&q=80",
      "https://images.unsplash.com/photo-1553413077-190dd305871c?w=1600&h=900&fit=crop&q=80",
      "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?w=1600&h=900&fit=crop&q=80"
    ],
    tags: ["Sanierung", "Logistik", "LED-Umrüstung"],
    status: "Abgeschlossen",
    highlight: "50.000 m² Hallenfläche",
    leistungen: [
      "Bestandsaufnahme und Lichtplanung",
      "LED-Umrüstung Bestandsbeleuchtung",
      "Erweiterung um 12.000 Lichtpunkte",
      "Projektleitung und Koordination",
      "Abnahme und Dokumentation"
    ],
    technischeDaten: {
      auftraggeber: "Amazon Deutschland",
      projektvolumen: "850.000 €",
      zeitraum: "Januar 2024 – August 2024",
      leistungsphase: "LP 3-8",
      flaeche: "50.000 m²"
    }
  },
  {
    id: "siemens-campus-erlangen",
    slug: "siemens-campus-erlangen",
    title: "Siemens Campus Erlangen",
    subtitle: "Gebäude 7 – Bürokomplex",
    shortDescription: "Hochwertige Bürobeleuchtung mit Human Centric Lighting für ein modernes Bürogebäude auf dem Siemens Campus.",
    description: "Implementierung eines innovativen Human Centric Lighting Konzepts für optimale Arbeitsbedingungen. Das System passt Farbtemperatur und Helligkeit dynamisch an den Tagesverlauf an und fördert so Wohlbefinden und Produktivität der Mitarbeiter.",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&h=900&fit=crop&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&h=900&fit=crop&q=80",
      "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1600&h=900&fit=crop&q=80",
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&h=900&fit=crop&q=80"
    ],
    tags: ["Neubau", "Büro", "Smart Building"],
    status: "Abgeschlossen",
    highlight: "Human Centric Lighting",
    leistungen: [
      "Lichtplanung nach DIN EN 12464-1",
      "Human Centric Lighting Konzept",
      "KNX/DALI Gateway Integration",
      "Fachbauleitung",
      "Inbetriebnahme und Parametrierung"
    ],
    technischeDaten: {
      auftraggeber: "Siemens Real Estate",
      projektvolumen: "2.1 Mio. €",
      zeitraum: "September 2023 – Juni 2024",
      leistungsphase: "LP 4-8",
      flaeche: "8.500 m²"
    }
  }
];

// ============ GRID PATTERN ============
function GridPattern({ dark = true }) {
  const color = dark 
    ? "var(--tga-border)" 
    : "rgba(0,0,0,0.035)";
  
  return (
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `
          linear-gradient(to right, ${color} 1px, transparent 1px),
          linear-gradient(to bottom, ${color} 1px, transparent 1px)
        `,
        backgroundSize: `${VISUAL_GRID}px ${VISUAL_GRID}px`,
      }}
    />
  );
}

// ============ LOGO ============
function TGAoneLogo() {
  return (
    <a 
      href="/"
      className="proj-logo-link"
      style={{ 
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none'
      }}
    >
      <img 
        src="/tgaone_black.svg"
        alt="TGAone" 
        className="proj-logo"
        style={{ 
          height: '48px',
          width: 'auto',
          objectFit: 'contain',
          display: 'block',
          transition: 'opacity 0.2s ease'
        }}
      />
      <style>{`
        .proj-logo-link:hover .proj-logo {
          opacity: 0.9;
        }
        .proj-logo {
          height: 48px !important;
        }
        @media (min-width: 768px) {
          .proj-logo {
            height: 64px !important;
          }
        }
        @media (min-width: 1024px) {
          .proj-logo {
            height: 80px !important;
          }
        }
      `}</style>
    </a>
  );
}

// ============ STAT ITEM ============
// Visuell stärker - große Zahlen, klare Hierarchie
function StatItem({ value, label, icon: Icon }) {
  return (
    <div className="text-center">
      <div 
        className="font-black text-white text-[36px] md:text-[48px] lg:text-[56px] leading-none tracking-tight"
      >
        {value}
      </div>
      <div 
        className="text-white/40 font-medium text-[13px] md:text-[14px] uppercase tracking-wider mt-2"
      >
        {label}
      </div>
    </div>
  );
}

// ============ PROJECT CARD - VISUAL UPGRADE ============
// Große Bilder, klare Hierarchie, Referenzwirkung
function ProjectCard({ project, featured = false }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <Link 
      to={`/projekte/${project.slug}`}
      className={`group block relative overflow-hidden bg-white w-full ${featured ? 'md:col-span-2' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container - größeres Aspect Ratio für Impact */}
      <div 
        className="relative overflow-hidden w-full"
        style={{ aspectRatio: featured ? '21/9' : '16/10' }}
      >
        <img 
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 ease-out"
          style={{ 
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
          }}
        />
        
        {/* Gradient Overlay - immer sichtbar für Lesbarkeit */}
        <div 
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
        />
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1.5 bg-white text-black font-semibold text-[11px] uppercase tracking-wider px-3 py-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {project.status}
          </span>
        </div>
        
        {/* Highlight Badge */}
        <div className="absolute top-4 right-4">
          <span className="bg-black/60 backdrop-blur-sm text-white font-medium text-[12px] px-3 py-1.5">
            {project.highlight}
          </span>
        </div>
        
        {/* Content Overlay - unten */}
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          {/* Subtitle */}
          <p className="text-white/60 font-medium text-[12px] uppercase tracking-wider mb-1">
            {project.subtitle}
          </p>
          
          {/* Title */}
          <h3 className={`font-bold text-white ${featured ? 'text-[24px] md:text-[32px]' : 'text-[20px] md:text-[24px]'} leading-tight mb-3`}>
            {project.title}
          </h3>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {project.tags.map((tag, idx) => (
              <span 
                key={idx}
                className="bg-white/10 backdrop-blur-sm text-white/90 font-medium text-[11px] px-2.5 py-1"
              >
                {tag}
              </span>
            ))}
          </div>
          
          {/* CTA */}
          <div 
            className="inline-flex items-center gap-2 text-white font-semibold text-[14px] transition-all duration-300"
            style={{ 
              transform: isHovered ? 'translateX(8px)' : 'translateX(0)',
              opacity: isHovered ? 1 : 0.8
            }}
          >
            Projekt ansehen
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ============ MAIN COMPONENT ============
export default function ProjekteReferenz() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-['Inter',system-ui,sans-serif] overflow-x-hidden">
      {/* Global Mobile Styles */}
      <style>{`
        html, body {
          overflow-x: hidden;
          max-width: 100vw;
        }
      `}</style>
      
      {/* ============ NAVIGATION ============ */}
      {/* Mobile: 80px, Desktop: 128px - Logo vertikal zentriert */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
      >
        <style>{`
          .proj-nav-wrapper {
            height: 80px;
            padding: 16px 0;
          }
          @media (min-width: 768px) {
            .proj-nav-wrapper {
              height: 128px;
              padding: 24px 0;
            }
          }
        `}</style>
        <div 
          className="proj-nav-wrapper mx-auto"
          style={{ maxWidth: '1120px' }}
        >
          <div className="flex items-center justify-between h-full px-5 md:px-12">
            <div className="flex items-center">
              <TGAoneLogo />
            </div>
            
            {/* Desktop Nav - Sichtbar ab lg (1024px) */}
            <div className="hidden lg:flex items-center" style={{ gap: '32px' }}>
              <Link 
                to="/"
                className="text-sm font-medium transition-colors text-[#6b7280] hover:text-[#1a1a1a]"
              >
                Startseite
              </Link>
              <Link 
                to="/projekte"
                className="text-sm font-medium transition-colors text-[#1a1a1a]"
              >
                Projekte
              </Link>
              <div className="w-px h-6 bg-[#e5e5e5]" />
              <Link 
                to="/login"
                className="text-sm font-medium transition-colors text-[#6b7280] hover:text-[#1a1a1a]"
              >
                Interner Bereich
              </Link>
            </div>
            
            {/* Mobile Burger Menu Button - Sichtbar unter lg (1024px) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 -mr-2 text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Menü öffnen"
              data-testid="mobile-menu-open"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>
      
      {/* ============ MOBILE SLIDE-IN MENU ============ */}
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 z-[60] transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setMobileMenuOpen(false)}
        data-testid="mobile-menu-overlay"
      />
      
      {/* Slide-In Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-[280px] max-w-[85vw] bg-[#0a0a0a] z-[70] transform transition-transform duration-300 ease-out lg:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu-panel"
      >
        {/* Header mit Close Button */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <span className="text-white/50 text-sm font-medium uppercase tracking-wider">Menü</span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 -mr-2 text-white/70 hover:text-white transition-colors"
            aria-label="Menü schließen"
            data-testid="mobile-menu-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Menu Items */}
        <nav className="p-5">
          <ul className="space-y-1">
            {[
              { to: "/#leistungen", label: "Leistungen" },
              { to: "/#partner", label: "Partner" },
              { to: "/#arbeitsweise", label: "Arbeitsweise" },
              { to: "/#kontakt", label: "Kontakt" },
              { to: "/projekte", label: "Projekte" },
            ].map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 px-4 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all text-[15px] font-medium"
                >
                  {item.label}
                </Link>
              </li>
            ))}
            
            {/* Divider */}
            <li className="py-3">
              <div className="h-px bg-white/10" />
            </li>
            
            {/* Interner Bereich */}
            <li>
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-4 text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-all text-[15px] font-medium"
              >
                Interner Bereich
              </Link>
            </li>
          </ul>
        </nav>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-5 border-t border-white/10">
          <p className="text-white/30 text-xs text-center">© 2026 TGAone</p>
        </div>
      </div>
      
      {/* ============ HERO SECTION - VISUAL UPGRADE ============ */}
      {/* Größerer Hero mit mehr Impact */}
      <section 
        className="relative bg-black overflow-hidden"
      >
        <style>{`
          .proj-hero-upgraded {
            padding-top: 140px;
            padding-bottom: 80px;
          }
          @media (min-width: 768px) {
            .proj-hero-upgraded {
              padding-top: 200px;
              padding-bottom: 100px;
            }
          }
        `}</style>
        <div className="proj-hero-upgraded">
          <GridPattern dark={true} />
          
          <div 
            className="mx-auto relative z-10 px-5 md:px-12"
            style={{ maxWidth: '1120px' }}
          >
            {/* Overline */}
            <p className="text-white/40 font-medium text-[11px] md:text-[12px] uppercase tracking-[0.2em] mb-4">
              Referenzen & Projekte
            </p>
            
            {/* Headline - größer und impactvoller */}
            <h1 
              className="font-black text-white tracking-tight text-[32px] leading-[1.1] md:text-[48px] lg:text-[64px] mb-6"
            >
              Wir machen<br />
              <span className="text-white/40">große Sachen.</span>
            </h1>
            
            <p 
              className="text-white/50 font-light text-[16px] md:text-[18px] lg:text-[20px] leading-relaxed"
              style={{ maxWidth: '500px' }}
            >
              Technische Gebäudeausrüstung für Industrie, Gewerbe und öffentliche Auftraggeber.
            </p>
          </div>
        </div>
      </section>
      
      {/* ============ STATS SECTION - VISUAL UPGRADE ============ */}
      {/* Größere Zahlen, mehr visuelle Präsenz */}
      <section 
        className="relative bg-black overflow-hidden border-t border-white/10 py-16 md:py-20"
      >
        <GridPattern dark={true} />
        
        <div 
          className="mx-auto relative z-10 px-5 md:px-12"
          style={{ maxWidth: '1120px' }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16">
            <StatItem value="120+" label="PROJEKTE" />
            <StatItem value="48" label="JAHRE ERFAHRUNG" />
            <StatItem value="100%" label="ZUFRIEDENE KUNDEN" />
            <StatItem value=">138.000" label="INSTALLIERTE LEUCHTEN" />
          </div>
        </div>
      </section>
      
      {/* ============ CTA SECTION - VISUAL UPGRADE ============ */}
      <section 
        className="relative bg-black overflow-hidden py-20 md:py-28"
      >
        <GridPattern dark={true} />
        
        <div 
          className="mx-auto relative z-10 text-center px-5 md:px-12"
          style={{ maxWidth: '800px' }}
        >
          <p className="text-white/30 font-medium text-[11px] uppercase tracking-[0.2em] mb-4">
            Nächstes Projekt
          </p>
          
          <h2 
            className="font-black text-white tracking-tight text-[28px] md:text-[40px] lg:text-[48px] mb-6 leading-tight"
          >
            Ihr Projekt ist<br />unser nächstes.
          </h2>
          
          <p 
            className="text-white/40 mx-auto text-[16px] md:text-[18px] leading-relaxed mb-10"
            style={{ maxWidth: '450px' }}
          >
            Sprechen Sie uns an – wir freuen uns auf Ihre Anfrage.
          </p>
          
          {/* CTA Button */}
          <Link 
            to="/#kontakt"
            className="w-full sm:w-auto inline-flex items-center justify-center bg-white text-black font-bold hover:bg-white/90 transition-all h-14 px-10 gap-2 text-[15px] tracking-wide"
          >
            Kontakt aufnehmen
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      {/* ============ FOOTER ============ */}
      <footer className="bg-black border-t border-white/10 relative overflow-hidden py-6">
        <div className="mx-auto relative z-10 px-5 md:px-12" style={{ maxWidth: '1120px' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-sm">
              <Link to="/kontakt" className="text-white/60 hover:text-white transition-colors">Kontakt</Link>
              <Link to="/impressum" className="text-white/40 hover:text-white/70 transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="text-white/40 hover:text-white/70 transition-colors">Datenschutz</Link>
            </div>
            
            <div className="text-white/30 text-sm">
              © 2026 TGAone
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
